/*
 * Copyright (c)  CYBERKOALA LLC 2025.
 * Proprietary License.
 * Redistribution and use in source and binary forms, with or without
 * modification, are restricted.
 */

import { useSettings } from '@/context/settings-context/settings-context';
import React, { useEffect, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { useCall } from '@/context/call-context/use-call.ts';
import { ActionTooltip } from '@/components/action-tooltip.tsx';
import { cn } from '@/lib/utils.ts';
import { Check, HeadphonesIcon } from 'lucide-react';

interface AudioOutputSelectorProps {
  iconSizeDocked?: number;
  iconSize?: number;
}

/**
 * Note: setting AudioOutput is not supported on some browsers. See setSinkId
 * only Desktop browsers are supported:
 * Chrome 49
 * Edge 17
 * Firefox 116
 * Opera 36
 * Safari 18.4
 * @param iconSizeDocked
 * @param iconSize
 * @constructor
 */
const AudioOutputSelector: React.FC<AudioOutputSelectorProps> = ({
  iconSizeDocked = 24,
  iconSize = 16,
}) => {
  const {
    // selectedAudioOutputDeviceId,
    // setSelectedAudioOutputDeviceId,
    audioOutputError,
    setAudioOutputError,
  } = useSettings();
  const { docked } = useCall();
  const [selectedAudioOutputDeviceId, setSelectedAudioOutputDeviceId] = useState<string | null>('default');
  let room = useRoomContext();
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isActiveOutput = (deviceId: string) => {
    const isDefaultOption = deviceId === 'default';

    return (
      deviceId === selectedAudioOutputDeviceId ||
      (isDefaultOption && selectedAudioOutputDeviceId === 'default')
    );
  };

  /**
   * Load & update device list
   */
  useEffect(() => {
    const handler = async () => {
      try {
        const list = await navigator.mediaDevices.enumerateDevices();
        const outputs = list.filter((d) => d.kind === 'audiooutput');
        setDevices(outputs);
      } catch (e) {
        setAudioOutputError('Failed to access media devices');
      }
    };

    if (typeof window !== 'undefined') {
      handler();
    }
    navigator.mediaDevices.addEventListener('devicechange', handler);
    return () => navigator.mediaDevices.removeEventListener('devicechange', handler);
  }, []);

  /**
   * Apply selected output device to LiveKit
   * This avoids calling switchActiveDevice directly inside UI handlers.
   */
  useEffect(() => {
    if (!room) return;
    if (!selectedAudioOutputDeviceId) return;

    if (room.state !== 'connected') return;

    /**
     * Note: setting AudioOutput is not supported on some browsers. See setSinkId
     * ONLY desktop browsers are supported:
     * Chrome 49
     * Edge 17
     * Firefox 116
     * Opera 36
     * Safari 18.4
     */
    room.switchActiveDevice('audiooutput', selectedAudioOutputDeviceId).catch((err) => {
      setError('Не удалось переключить устройство вывода');
    });
  }, [selectedAudioOutputDeviceId, room]);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const handleSelect = (deviceId: string) => {
    setSelectedAudioOutputDeviceId(deviceId);
    localStorage.setItem('audioOutputDeviceId', deviceId);
    closeModal();
  };

  if (audioOutputError) {
    return <div className="text-red-500 text-sm">{audioOutputError}</div>;
  }

  // TODO: translate

  if (devices.length === 0) {
    return (
      <>
        <ActionTooltip label="Аудиовыход" side="top">
          <button
            onClick={() => setIsModalOpen(true)}
            className={cn(
              docked ? 'h-9' : 'h-6',
              'px-2 py-1 rounded-lg flex items-center justify-center opacity-70 cursor-not-allowed'
            )}
          >
            <HeadphonesIcon size={docked ? iconSizeDocked : iconSize} />
          </button>
        </ActionTooltip>

        {isModalOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
            onClick={() => setIsModalOpen(false)}
          >
            <div className="bg-background rounded-xl p-4 w-80" onClick={(e) => e.stopPropagation()}>
              <h2 className="text-lg font-semibold mb-4">Нет аудиовыходов</h2>

              <p className="text-neutral-500 text-sm mb-4">
                В вашей системе не обнаружены устройства аудиовыхода, либо их использование
                ограничено политиками безопасности.
              </p>

              <button
                onClick={() => setIsModalOpen(false)}
                className="mt-2 w-full bg-neutral-500 hover:bg-neutral-600 py-2 rounded-lg"
              >
                Закрыть
              </button>
            </div>
          </div>
        )}
      </>
    );
  }
  if (devices.length > 0) {
    return (
      <>
        <ActionTooltip label="Аудиовыход" side="top">
          <button
            onClick={openModal}
            className={cn(
              docked ? 'h-9' : 'h-6',
              'px-2 py-1 rounded-lg flex items-center justify-center'
            )}
          >
            <HeadphonesIcon size={docked ? iconSizeDocked : iconSize} />
          </button>
        </ActionTooltip>

        {isModalOpen && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center"
            onClick={closeModal}
          >
            <div
              className="bg-background
                border border-border
                rounded-xl
                p-4
                w-80
                shadow-x"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 className="text-lg font-semibold mb-4 text-foreground">Выбор аудиовыхода</h2>
              {error && <p className="text-red-500 text-sm mb-2">{error}</p>}
              <div className="flex flex-col gap-2">
                {devices.map((d) => {
                  const active = isActiveOutput(d.deviceId);

                  return (
                    <button
                      key={d.deviceId}
                      onClick={() => handleSelect(d.deviceId)}
                      className={cn(
                        'w-full px-3 py-2 rounded-md flex items-center justify-between transition-colors text-sm',
                        active
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'bg-muted text-foreground hover:bg-accent'
                      )}
                    >
                      <span className="truncate">{d.label || 'Unknown device'}</span>

                      {active && <Check className="w-4 h-4 shrink-0" />}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={closeModal}
                className="
                  mt-4
                  w-full
                  rounded-lg
                  bg-muted
                  hover:bg-accent
                  text-foreground
                  py-2
                  transition-colors
                "
              >
                Закрыть
              </button>
            </div>
          </div>
        )}
      </>
    );
  }
};

export default AudioOutputSelector;
